import { bordesDeVigencia } from '@practiclinic/core'
import { hoy } from '../datos/formato.js'
import type {
  AsignacionEspacioLocal,
  RecursoAfinidadLocal,
  RecursoHorarioLocal,
} from '../datos/db.js'

/**
 * La lógica pura de la vista Consultorios (T29).
 *
 * Personas elegibles para una celda, huecos entre tramos publicados y franjas
 * descubiertas: todo se deriva de lo que ya trae el canal de sync en Dexie,
 * sin red. La escritura (crear/publicar/cancelar) vive en los componentes;
 * acá nada toca el DOM ni un fetch.
 */

/** domingo = 0 … sábado = 6; convención de Postgres, igual que `recurso_horario`. */
export const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'] as const

export interface Franja {
  desde: number
  hasta: number
}

/** `HH:mm` de reloj → minutos desde la medianoche. */
export function aMinutos(hhmm: string): number {
  const [h, m] = hhmm.split(':')
  const total = Number(h) * 60 + Number(m)
  return Number.isFinite(total) ? total : 0
}

/** Minutos desde la medianoche → `HH:mm`. Ajusta al cierre antes de formatear. */
export function aReloj(minutos: number): string {
  const acotado = Math.max(0, Math.min(24 * 60, Math.round(minutos)))
  return `${String(Math.floor(acotado / 60)).padStart(2, '0')}:${String(acotado % 60).padStart(2, '0')}`
}

/** Semiabierto `[a,b)`: chocan si `a.desde < b.hasta && b.desde < a.hasta`. */
export function franjasSolapan(a: Franja, b: Franja): boolean {
  return a.desde < b.hasta && b.desde < a.hasta
}

/** Junta franjas contiguas o superpuestas en tramos disjuntos ordenados. */
export function unirFranjas(franjas: Franja[]): Franja[] {
  const ordenadas = [...franjas].sort((a, b) => a.desde - b.desde)
  const salida: Franja[] = []
  for (const f of ordenadas) {
    const ultima = salida[salida.length - 1]
    if (ultima && f.desde <= ultima.hasta) {
      ultima.hasta = Math.max(ultima.hasta, f.hasta)
    } else {
      salida.push({ desde: f.desde, hasta: f.hasta })
    }
  }
  return salida
}

/** Lo que queda de `base` después de quitar `quitar`. Se usa para las franjas descubiertas. */
function restarFranjas(base: Franja[], quitar: Franja[]): Franja[] {
  const unidas = unirFranjas(quitar)
  const salida: Franja[] = []
  for (const b of base) {
    let cursor = b.desde
    for (const q of unidas) {
      if (q.hasta <= cursor) continue
      if (q.desde >= b.hasta) break
      if (q.desde > cursor) salida.push({ desde: cursor, hasta: Math.min(b.hasta, q.desde) })
      cursor = Math.max(cursor, q.hasta)
    }
    if (cursor < b.hasta) salida.push({ desde: cursor, hasta: b.hasta })
  }
  return salida
}

/**
 * Los días que se muestran como columnas de la grilla semanal.
 *
 * Lunes a sábado siempre; el domingo sólo si alguna persona carga horario ese
 * día. Lunes = índice 1 en la convención Postgres.
 */
export function diasDeLaSemana(horarios: RecursoHorarioLocal[]): number[] {
  const usaDomingo = horarios.some((h) => h.dia_semana === 0)
  const dias = [1, 2, 3, 4, 5, 6]
  if (usaDomingo) dias.push(0)
  return dias
}

/** El tramo horario visible de la semana, acotado a las horas razonables de una clínica. */
export function rangoHoras(
  horarios: RecursoHorarioLocal[],
  piso = 7,
  techo = 23,
): { desde: number; hasta: number } {
  const minutos = horarios.flatMap((h) => [aMinutos(h.desde), aMinutos(h.hasta)])
  if (minutos.length === 0) return { desde: 8, hasta: 20 }
  const desde = Math.max(piso, Math.floor(Math.min(...minutos) / 60))
  const hasta = Math.min(techo, Math.max(desde + 1, Math.ceil(Math.max(...minutos) / 60)))
  return { desde, hasta }
}

/**
 * El bloque que se pinta al elegir a una persona: el horario completo de esa
 * persona para el día que cubre la hora clickeada, listo para recortar con
 * los bordes. `horarios` tiene que ser únicamente los de esa persona.
 */
export function bloqueInicial(
  diaSemana: number,
  hora: number,
  horarios: RecursoHorarioLocal[],
): Franja | null {
  const propio = horarios.find(
    (h) =>
      h.dia_semana === diaSemana &&
      hora >= aMinutos(h.desde) &&
      hora < aMinutos(h.hasta),
  )
  if (!propio) return null
  return { desde: aMinutos(propio.desde), hasta: aMinutos(propio.hasta) }
}

export interface PersonaElegible {
  personaId: string
  nombre: string
  /** `null` = se puede elegir; si no, el motivo visible de por qué está deshabilitada. */
  motivo: string | null
}

/**
 * Las personas que se ofrecen al clickear una celda libre (día D, hora H,
 * consultorio E).
 *
 * Califica quien tiene `recurso_horario` de D que cubre H y no tiene otra
 * asignación publicada, no borrada, en otro espacio, el mismo día, con
 * solape semiabierto sobre la hora. Las que no califican salen deshabilitadas
 * con el motivo visible. Orden: afinidad fija con E, después preferida con E,
 * después el resto por nombre.
 */
export function elegiblesDeCelda(
  diaSemana: number,
  hora: number,
  espacioId: string,
  personas: { id: string; nombre: string }[],
  horarios: RecursoHorarioLocal[],
  afinidades: RecursoAfinidadLocal[],
  asignaciones: AsignacionEspacioLocal[],
  espacios: { id: string; nombre: string }[],
): PersonaElegible[] {
  const enOtroEspacio = asignaciones.filter(
    (a) =>
      a.estado === 'publicada' &&
      !a.deleted_at &&
      a.espacio_id !== espacioId &&
      a.dia_semana === diaSemana &&
      asignacionCubreFecha(a, hoy()),
  )
  const horaFranja: Franja = { desde: hora, hasta: hora + 1 }

  const marcadas = personas.map((p): PersonaElegible => {
    const cubreHora = horarios.some(
      (h) =>
        h.recurso_id === p.id &&
        h.dia_semana === diaSemana &&
        hora >= aMinutos(h.desde) &&
        hora < aMinutos(h.hasta),
    )
    if (!cubreHora) {
      return {
        personaId: p.id,
        nombre: p.nombre,
        motivo: `sin horario los ${DIAS[diaSemana]!.toLowerCase()}`,
      }
    }
    const otra = enOtroEspacio.find(
      (a) =>
        a.persona_id === p.id &&
        franjasSolapan({ desde: aMinutos(a.desde), hasta: aMinutos(a.hasta) }, horaFranja),
    )
    if (otra) {
      const nombre = espacios.find((e) => e.id === otra.espacio_id)?.nombre ?? 'otro consultorio'
      return { personaId: p.id, nombre: p.nombre, motivo: `ya asignada a ${nombre} en este horario` }
    }
    return { personaId: p.id, nombre: p.nombre, motivo: null }
  })

  const peso = (personaId: string): number => {
    const af = afinidades.find(
      (a) => a.persona_id === personaId && a.espacio_id === espacioId && !a.deleted_at,
    )
    if (af?.modo === 'fijo') return 0
    if (af?.modo === 'preferido') return 1
    return 2
  }

  return marcadas.sort((a, b) => peso(a.personaId) - peso(b.personaId) || a.nombre.localeCompare(b.nombre))
}

/** Los huecos entre tramos publicados de un mismo consultorio (un día dado). */
export function huecosEntreTramos(tramos: Franja[]): Franja[] {
  const ordenados = [...tramos].sort((a, b) => a.desde - b.desde)
  const huecos: Franja[] = []
  for (let i = 1; i < ordenados.length; i++) {
    const previo = ordenados[i - 1]!
    const actual = ordenados[i]!
    if (actual.desde > previo.hasta) huecos.push({ desde: previo.hasta, hasta: actual.desde })
  }
  return huecos
}

export interface FranjaDescubierta extends Franja {
  personaId: string
  nombre: string
  diaSemana: number
}

/**
 * Personas con horario cuyo tramo no está cubierto por ninguna asignación
 * publicada: la alerta de "horario sin consultorio" visible sin entrar a cada
 * celda.
 */
export function franjasDescubiertas(
  personas: { id: string; nombre: string }[],
  horarios: RecursoHorarioLocal[],
  asignaciones: AsignacionEspacioLocal[],
): FranjaDescubierta[] {
  // Sólo cuentan las publicadas cuya vigencia cubre hoy: una plantilla vencida
  // ni bloquea a la persona ni tapa su franja descubierta.
  const publicadas = asignaciones.filter(
    (a) => a.estado === 'publicada' && !a.deleted_at && asignacionCubreFecha(a, hoy()),
  )
  const salida: FranjaDescubierta[] = []

  for (const persona of personas) {
    const suyas = horarios.filter((h) => h.recurso_id === persona.id)
    const porDia = new Map<number, RecursoHorarioLocal[]>()
    for (const h of suyas) {
      const lista = porDia.get(h.dia_semana) ?? []
      lista.push(h)
      porDia.set(h.dia_semana, lista)
    }

    for (const [dia, delDia] of porDia) {
      const cubierto = publicadas
        .filter((a) => a.persona_id === persona.id && a.dia_semana === dia)
        .map((a): Franja => ({ desde: aMinutos(a.desde), hasta: aMinutos(a.hasta) }))
      const franjas = delDia.map((h): Franja => ({ desde: aMinutos(h.desde), hasta: aMinutos(h.hasta) }))
      for (const resto of restarFranjas(franjas, cubierto)) {
        salida.push({ personaId: persona.id, nombre: persona.nombre, diaSemana: dia, ...resto })
      }
    }
  }

  return salida.sort(
    (a, b) => a.nombre.localeCompare(b.nombre) || a.diaSemana - b.diaSemana || a.desde - b.desde,
  )
}

/** ¿Esta asignación de plantilla cubre la fecha `YYYY-MM-DD` que se está mirando? */
export function asignacionCubreFecha(a: AsignacionEspacioLocal, dia: string): boolean {
  const { vigenciaDesde, vigenciaHasta } = bordesDeVigencia(a.vigencia)
  const desde = vigenciaDesde.toISOString().slice(0, 10)
  if (dia < desde) return false
  if (vigenciaHasta !== null) {
    const hasta = vigenciaHasta.toISOString().slice(0, 10)
    if (dia >= hasta) return false
  }
  return true
}